import { useEffect, useRef, useState, useMemo } from 'react';
import { cn } from '@/utils/cn';
import type { VisualizationConfig } from '@/types/visualization';
import type { LandmarkDeviationWithCoords } from '@/types/visualization';
import type { RegionHull, FrameScore } from '@/types/api';
import GhostEchoOverlay from './GhostEchoOverlay';
import DeviationVectorsOverlay from './DeviationVectorsOverlay';
import ThermalSilhouetteOverlay from './ThermalSilhouetteOverlay';
import DifferentialSkeletonOverlay from './DifferentialSkeletonOverlay';
import AlignmentRingsOverlay from './AlignmentRingsOverlay';
import ParticleAuraOverlay from './ParticleAuraOverlay';
import BodyRegionOverlay from '../evaluation/BodyRegionOverlay';

// ---------------------------------------------------------------------------
// Letterbox helper — mirrors the same function in GoalOverlayLayer
// ---------------------------------------------------------------------------

/**
 * Compute the actual rendered area of the video within its container,
 * accounting for aspect ratio differences (letterboxing / pillarboxing).
 *
 * When videoElement is unavailable or has no intrinsic size, the full
 * container dimensions are returned with zero offsets so the overlay
 * renders identically to before (backward-compatible for landscape videos).
 */
function computeVideoRenderRect(
  containerWidth: number,
  containerHeight: number,
  videoElement: HTMLVideoElement | null,
): { offsetX: number; offsetY: number; renderWidth: number; renderHeight: number } {
  if (!videoElement || !videoElement.videoWidth || !videoElement.videoHeight) {
    return { offsetX: 0, offsetY: 0, renderWidth: containerWidth, renderHeight: containerHeight };
  }

  const videoAspect = videoElement.videoWidth / videoElement.videoHeight;
  const containerAspect = containerWidth / containerHeight;

  let renderWidth: number;
  let renderHeight: number;
  let offsetX: number;
  let offsetY: number;

  if (videoAspect > containerAspect) {
    // Video is wider than the container — letterbox top/bottom
    renderWidth = containerWidth;
    renderHeight = containerWidth / videoAspect;
    offsetX = 0;
    offsetY = (containerHeight - renderHeight) / 2;
  } else {
    // Video is taller than the container — pillarbox left/right
    renderHeight = containerHeight;
    renderWidth = containerHeight * videoAspect;
    offsetX = (containerWidth - renderWidth) / 2;
    offsetY = 0;
  }

  return { offsetX, offsetY, renderWidth, renderHeight };
}

interface UnifiedPoseOverlayProps {
  videoElement: HTMLVideoElement | null;
  visualizationConfig: VisualizationConfig;

  // Pose data
  templateLandmarks?: Array<{ x: number; y: number; visibility?: number }>;
  athleteLandmarks?: Array<{ x: number; y: number; visibility?: number }>;
  landmarkDeviations?: LandmarkDeviationWithCoords[];

  // Context
  currentFrame: number;
  totalFrames: number;

  // Frame-level scores (fallback when per-landmark data is sparse)
  frameScores?: FrameScore[];

  // Existing region data (for compatibility)
  regionHulls?: RegionHull[];

  /**
   * Whether the caller's "Regions" control is ON.
   *
   * Owner report 2026-08-02: "Pose has the combination of both Region hulls AND
   * the skeleton". The inner BodyRegionOverlay used to render on
   * `applyToRegions && regionHulls.length > 0 && mode !== 'off'` alone — a
   * config default (applyToRegions=true), NOT the user's Regions pill. So hulls
   * painted over the skeleton whenever Pose was on and the pill was dead on the
   * athlete panel. Callers now pass their pill state here.
   *
   * Defaults to true so existing callers keep their current behaviour.
   */
  showRegions?: boolean;

  mirrored?: boolean;
  className?: string;
}

/**
 * UnifiedPoseOverlay is the main wrapper that renders the appropriate overlay
 * based on the visualization mode selected in the config.
 *
 * Supported modes:
 * - 'off': No overlay
 * - 'ghost_echo': Template pose as translucent overlay
 * - 'deviation_vectors': Arrows showing movement corrections
 * - 'thermal_silhouette': Heat map coloring by alignment quality
 * - 'alignment_rings': Target zones around landmarks
 * - 'differential_skeleton': Skeleton colored by bone alignment
 * - 'particle_aura': Animated particles showing form quality
 */
function UnifiedPoseOverlay({
  videoElement,
  visualizationConfig,
  templateLandmarks,
  athleteLandmarks,
  landmarkDeviations = [],
  currentFrame,
  totalFrames,
  frameScores = [],
  regionHulls = [],
  showRegions = true,
  mirrored = false,
  className,
}: UnifiedPoseOverlayProps) {
  // CHOKE-POINT NORMALIZATION (2026-08-02, Zoe athlete-skeleton bug): the `= []`
  // parameter default above only fires on `undefined`. Evals WITHOUT per-landmark
  // deviation data pass an explicit `null`, which flowed verbatim into every
  // mode sub-overlay whose draw loop calls .filter/.find on it. Differential
  // skeleton then threw inside its try/catch and rendered a silently BLANK
  // canvas (hulls still painted → "Pose shows Regions instead"). Normalize once
  // here so every child receives a real array.
  const safeDeviations = useMemo(() => landmarkDeviations ?? [], [landmarkDeviations]);
  const canvasRef = useRef<HTMLCanvasElement>(null!);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Sync canvas size with video element dimensions
  useEffect(() => {
    if (!videoElement) return;

    const updateDimensions = () => {
      setDimensions({
        width: videoElement.clientWidth,
        height: videoElement.clientHeight,
      });
    };

    // Initial update
    updateDimensions();

    // Update on resize
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(videoElement);

    // Update on metadata load
    videoElement.addEventListener('loadedmetadata', updateDimensions);

    return () => {
      resizeObserver.disconnect();
      videoElement.removeEventListener('loadedmetadata', updateDimensions);
    };
  }, [videoElement]);

  // Get frame score for current frame (used as fallback when per-landmark data is sparse)
  const currentFrameScore = useMemo(
    () => frameScores?.find((fs) => fs.frame_index === currentFrame)?.score ?? null,
    [frameScores, currentFrame]
  );

  const { mode, intensity, applyToRegions } = visualizationConfig;

  // Compute the actual video render rectangle within the container.
  // This is recalculated on every render; sub-renderers will receive the
  // render-area dimensions + offsets rather than the full container size.
  const { offsetX, offsetY, renderWidth, renderHeight } = computeVideoRenderRect(
    dimensions.width,
    dimensions.height,
    videoElement,
  );

  // Clear canvas when mode is 'off'
  useEffect(() => {
    if (mode === 'off' && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, dimensions.width, dimensions.height);
      }
    }
  }, [mode, dimensions]);

  // Don't render if dimensions are invalid
  if (dimensions.width === 0 || dimensions.height === 0) {
    return null;
  }

  return (
    <div className={cn('absolute inset-0 pointer-events-none', className)}>
      {/* Main visualization overlay based on mode */}
      {mode === 'ghost_echo' && templateLandmarks && (
        <GhostEchoOverlay
          canvasRef={canvasRef}
          templateLandmarks={templateLandmarks}
          athleteLandmarks={athleteLandmarks}
          width={renderWidth}
          height={renderHeight}
          offsetX={offsetX}
          offsetY={offsetY}
          containerWidth={dimensions.width}
          containerHeight={dimensions.height}
          intensity={intensity}
        />
      )}

      {mode === 'deviation_vectors' && (
        <DeviationVectorsOverlay
          canvasRef={canvasRef}
          landmarkDeviations={safeDeviations}
          width={renderWidth}
          height={renderHeight}
          offsetX={offsetX}
          offsetY={offsetY}
          containerWidth={dimensions.width}
          containerHeight={dimensions.height}
          intensity={intensity}
          currentFrame={currentFrame}
        />
      )}

      {mode === 'thermal_silhouette' && athleteLandmarks && (
        <ThermalSilhouetteOverlay
          canvasRef={canvasRef}
          landmarks={athleteLandmarks}
          landmarkDeviations={safeDeviations}
          width={renderWidth}
          height={renderHeight}
          offsetX={offsetX}
          offsetY={offsetY}
          containerWidth={dimensions.width}
          containerHeight={dimensions.height}
          intensity={intensity}
          currentFrame={currentFrame}
          fallbackScore={currentFrameScore}
        />
      )}

      {mode === 'differential_skeleton' && athleteLandmarks && (
        <DifferentialSkeletonOverlay
          canvasRef={canvasRef}
          landmarks={athleteLandmarks}
          landmarkDeviations={safeDeviations}
          width={renderWidth}
          height={renderHeight}
          offsetX={offsetX}
          offsetY={offsetY}
          containerWidth={dimensions.width}
          containerHeight={dimensions.height}
          intensity={intensity}
          currentFrame={currentFrame}
          fallbackScore={currentFrameScore}
        />
      )}

      {mode === 'alignment_rings' && templateLandmarks && athleteLandmarks && (
        <AlignmentRingsOverlay
          canvasRef={canvasRef}
          templateLandmarks={templateLandmarks}
          athleteLandmarks={athleteLandmarks}
          width={renderWidth}
          height={renderHeight}
          offsetX={offsetX}
          offsetY={offsetY}
          containerWidth={dimensions.width}
          containerHeight={dimensions.height}
          intensity={intensity}
        />
      )}

      {mode === 'particle_aura' && athleteLandmarks && (
        <ParticleAuraOverlay
          canvasRef={canvasRef}
          landmarks={athleteLandmarks}
          landmarkDeviations={safeDeviations}
          width={renderWidth}
          height={renderHeight}
          offsetX={offsetX}
          offsetY={offsetY}
          containerWidth={dimensions.width}
          containerHeight={dimensions.height}
          intensity={intensity}
          currentFrame={currentFrame}
          fallbackScore={currentFrameScore}
        />
      )}

      {/* Optionally also render body region overlay if enabled and we have region hulls.
          showLabels and showScore are intentionally hardcoded to false here: the region
          hull caption ("Lead Arm: 95%") is confusing when overlaid on top of the ghost
          echo or other visualization modes. Region boundaries remain visible via the
          hull colour fill/stroke. */}
      {showRegions && applyToRegions && regionHulls.length > 0 && mode !== 'off' && (
        <BodyRegionOverlay
          videoElement={videoElement}
          regionHulls={regionHulls}
          currentFrame={currentFrame}
          totalFrames={totalFrames}
          width={dimensions.width}
          height={dimensions.height}
          showOverlay={true}
          overlayOpacity={intensity / 200} // Reduce opacity when combined with other overlays
          showHulls={true}
          showLabels={false}
          showScore={false}
          mirrored={mirrored}
        />
      )}
    </div>
  );
}

export default UnifiedPoseOverlay;
